import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Pause, RotateCcw, Zap, Square, Loader2, Info, X, Settings, ChevronDown, Minus, Plus } from 'lucide-react';
import { ReaderStatus, SessionResult } from '../types';
import { useRSVPReader, RampConfig } from '../hooks/useRSVPReader';
import { useFeatureGating } from '../hooks/useFeatureGating';
import { useStoryContent } from '../hooks/useStoryContent';
import { ContentSection } from '../types/content';

export type { RampConfig } from '../hooks/useRSVPReader';

// Generate test content for guest mode
const generateTestContent = () => {
  const paragraph = "The quick brown fox jumps over the lazy dog. Speed reading is a skill that allows you to absorb information rapidly. By eliminating subvocalization and reducing eye movement, you can significantly increase your words per minute. The Optimal Recognition Point is critical for this process. It acts as a pivot for your eyes, keeping them stationary while the text flows. This method is called Rapid Serial Visual Presentation, or RSVP. Mastering this technique takes practice, but the results are rewarding. Imagine finishing a book in a fraction of the time.";
  
  let content = "";
  for(let i=0; i<15; i++) {
    content += paragraph + "\n\n";
  }
  return content;
};


interface RSVPReaderProps {
  content?: string;
  sections?: ContentSection[]; // New: sections with contentPath for lazy loading
  categoryId?: string; // Category prefix for content loading (e.g., 'scifi', 'historical')
  initialWpm: number;
  onComplete: (result: SessionResult) => void;
  onWpmChange: (wpm: number) => void;
  rampConfig: RampConfig;
  progressMode?: 'word' | 'time';
  autoStart?: boolean;
  allowSectionNavigation?: boolean;
  allowTimeSkip?: boolean;
  enableKeyboardShortcuts?: boolean;
  isAuthenticated?: boolean;
  userTier?: 'guest' | 'starter' | 'reader' | 'ultra'; // Added for tier-based gating
  guestSessionsRemaining?: number;
  bookId?: string;        // Optional book identifier for progress tracking
  bookTitle?: string;     // Optional book title for display
  initialWordIndex?: number; // starting word index for resuming
  format?: string;        // Optional file format (txt, epub, pdf, etc.)
}

const RSVPReader: React.FC<RSVPReaderProps> = ({
  content,
  sections,
  categoryId,
  initialWpm,
  onComplete,
  onWpmChange,
  rampConfig,
  progressMode = 'word',
  autoStart = false,
  allowSectionNavigation = true,
  allowTimeSkip = false,
  enableKeyboardShortcuts = true,
  isAuthenticated = false,
  userTier = 'guest',
  guestSessionsRemaining,
  bookId,
  bookTitle,
  initialWordIndex,
  format
}) => {
  const navigate = useNavigate();

  // Lazy loading state
  const [loadedSections, setLoadedSections] = useState<ContentSection[]>([]);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);
  
  // Lazy content loading hook - pass categoryId for correct content path
  const { loadSectionContent, preloadSections } = useStoryContent(categoryId);
  
  // Get tier-based feature flags
  const { 
    canControlSpeed,
    canNavigateSections,
    canSkipTime,
    canUseKeyboardShortcuts,
    isForcedRamp
  } = useFeatureGating();
  
  // Determine if user is in guest mode (guest tier)
  const isGuestMode = userTier === 'guest';
  
  // Determine what content to use based on auth and available content
  const hasContentString = !!(content?.trim().length);
  const hasSections = !!(sections?.length);
  // Check if sections have inline content (e.g. from uploaded files) — at least one section must have content
  const sectionsHaveInlineContent = hasSections && sections!.some(s => s.content);
  // Check if ALL sections need lazy loading (none have inline content)
  const allSectionsNeedLoading = hasSections && sections!.every(s => !s.content && s.contentPath);
  // For guests: allow real content from selected stories
  // For authenticated with sections that have inline content: use sections directly
  // For authenticated with sections needing lazy loading: use loadedSections (undefined until loaded)
  // For authenticated with content string: use content
  // For authenticated with nothing: use test content
  const effectiveContent = (!hasContentString && !hasSections)
    ? generateTestContent()
    : (hasContentString ? content : undefined);
  const effectiveSections = sectionsHaveInlineContent
    ? sections!  // Use sections directly — they already have content
    : (allSectionsNeedLoading ? loadedSections : undefined);

  // Load section content when sections change
  useEffect(() => {
    if (!sections?.length) {
      setLoadedSections([]);
      setIsLoadingContent(false);
      return;
    }

    // If all sections already have inline content (e.g. from uploaded files), use them directly
    const allHaveContent = sections.every(s => s.content);
    if (allHaveContent) {
      setLoadedSections(sections);
      setIsLoadingContent(false);
      return;
    }

    let cancelled = false;

    async function loadContent() {
      setIsLoadingContent(true);
      setContentError(null);

      try {
        // Load content for all sections
        const loaded = await Promise.all(
          (sections || []).map(async (section) => {
            // If section already has content, use it
            if (section.content) {
              return section;
            }
            // Otherwise load from contentPath
            if (section.contentPath) {
              const content = await loadSectionContent(section);
              return { ...section, content };
            }
            return section;
          })
        );

        if (!cancelled) {
          setLoadedSections(loaded);
          setIsLoadingContent(false);
          
          // Preload next sections if available
          const sectionsWithPaths = (sections || []).filter(s => s.contentPath);
          if (sectionsWithPaths.length > 1) {
            preloadSections(sectionsWithPaths.slice(1));
          }
        }
      } catch (err) {
        if (!cancelled) {
          setContentError(err instanceof Error ? err.message : 'Failed to load content');
          setIsLoadingContent(false);
        }
      }
    }

    loadContent();

    return () => {
      cancelled = true;
    };
  }, [sections, isGuestMode, loadSectionContent, preloadSections]);

  // Use tier-based feature flags — canonical source of truth
  const userCanNavigate = canNavigateSections();
  const userCanSkip = canSkipTime();
  const userForcedRamp = isForcedRamp();
  const userCanUseKeyboard = canUseKeyboardShortcuts();
  const userCanControlSpeed = canControlSpeed();


  // ORP tooltip state — shown once per user on first reading session
  const [showOrpTooltip, setShowOrpTooltip] = useState(() => {
    return localStorage.getItem('flashread_orp_explained') !== 'true';
  });

  // Settings panel state - inline overlay when paused
  const [showSettings, setShowSettings] = useState(false);
  const [rampEnabled, setRampEnabled] = useState(false);
  const [rampStart, setRampStart] = useState(200);
  const [rampEnd, setRampEnd] = useState(500);
  const [rampDuration, setRampDuration] = useState(60);
  const [constantWpm, setConstantWpm] = useState(initialWpm);


  // Build effective ramp config based on mode
  const effectiveRampConfig: RampConfig = userForcedRamp ? {
    enabled: true,
    startWpm: 200,
    endWpm: 500,
    durationSeconds: 60
  } : {
    enabled: rampEnabled,
    startWpm: rampEnabled ? rampStart : constantWpm,
    endWpm: rampEnabled ? rampEnd : constantWpm,
    durationSeconds: rampDuration
  };

  // Handle session completion
  const handleCompletion = (result: SessionResult) => {
    onComplete(result);
    // No more loops - upsell handled by session counter in App.tsx
  };

  // Re-initialize useRSVPReader with effective config
  // Use constantWpm (user's slider choice) instead of initialWpm for authenticated users
  const effectiveInitialWpm = isAuthenticated && !rampEnabled ? constantWpm : initialWpm;
  
  const {
    currentIndex: idx,
    status: readerStatus,
    wpm: readerWpm,
    displayWord: readerDisplayWord,
    progress: readerProgress,
    showNavArrows: readerShowNavArrows,
    totalWords: readerTotalWords,
    togglePlay: readerTogglePlay,
    reset: readerReset,
    finishSession: readerFinishSession,
    jumpToSection: readerJumpToSection,
    skipTime: readerSkipTime,
    handleWpmChange: readerHandleWpmChange,
    adjustWpm: readerAdjustWpm
  } = useRSVPReader({
    content: effectiveSections?.length ? undefined : effectiveContent,
    sections: effectiveSections?.length ? effectiveSections : undefined,
    initialWpm: effectiveInitialWpm,
    onComplete: handleCompletion,
    onWpmChange,
    rampConfig: effectiveRampConfig,
    progressMode: userCanControlSpeed ? 'word' : 'time',
    autoStart: autoStart ?? false,
    allowSectionNavigation: userCanNavigate,
    allowTimeSkip: userCanSkip,
    enableKeyboardShortcuts: userCanUseKeyboard,
    bookId,
    chapterId: undefined, // Not needed at component level; determined internally from sections
    initialWordIndex,
    format,
  });

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newWpm = parseInt(e.target.value, 10);
    readerHandleWpmChange(newWpm);
  };

  // Auto-save on page unload (tab close, refresh, navigation away)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (readerStatus === ReaderStatus.PLAYING || readerStatus === ReaderStatus.PAUSED) {
        readerFinishSession();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [readerStatus, readerFinishSession]);

  // Show loading state while content is being fetched
  if (isLoadingContent) {
    return (
      <div className="w-full max-w-3xl mx-auto min-h-screen pt-16 pb-8 flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
          <p className="text-slate-600 dark:text-slate-400">Loading story...</p>
        </div>
      </div>
    );
  }

  // Show error state if content failed to load
  if (contentError) {
    return (
      <div className="w-full max-w-3xl mx-auto min-h-screen pt-16 pb-8 flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center max-w-md">
          <p className="text-red-600 dark:text-red-400 font-semibold mb-2">Failed to Load Content</p>
          <p className="text-red-500 dark:text-red-300 text-sm">{contentError}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200 rounded-lg hover:bg-red-200 dark:hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-3xl mx-auto min-h-screen pt-16 pb-8 flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
      {/* Guest Session Banner */}
      {guestSessionsRemaining !== undefined && guestSessionsRemaining > 0 && (
        <div className="text-center text-sm text-slate-500 dark:text-slate-400 mb-4">
          {guestSessionsRemaining} free stor{guestSessionsRemaining !== 1 ? 'ies' : 'y'} remaining ·
          <button onClick={() => { readerFinishSession(); navigate('/signup'); }} className="text-blue-600 dark:text-blue-400 hover:underline ml-1">
            Sign up to save progress
          </button>
        </div>
      )}
      {guestSessionsRemaining !== undefined && guestSessionsRemaining === 0 && (
        <div className="text-center text-sm text-amber-600 dark:text-amber-400 mb-4">
          This is your last free story ·
          <button onClick={() => { readerFinishSession(); navigate('/signup'); }} className="text-blue-600 dark:text-blue-400 hover:underline ml-1">
            Sign up to keep reading
          </button>
        </div>
      )}

      
      <div className="w-full max-w-3xl px-4 mb-4 flex flex-col items-center">
        {bookTitle && (
          <h1 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3 text-center">
            {bookTitle}
          </h1>
        )}
        <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2">
          RSVP Session
        </h2>
        <div className="text-4xl font-mono font-bold text-slate-800 dark:text-white">
          {readerWpm} <span className="text-2xl text-slate-500 dark:text-slate-400">WPM</span>
        </div>
      </div>
      
      <div className="w-full flex flex-col items-center">
          {/* Reader Display */}
          <div
            className="relative w-full h-48 md:h-56 bg-slate-100 dark:bg-slate-900 rounded-2xl shadow-2xl border-2 border-slate-300 dark:border-slate-600 flex items-center justify-center overflow-hidden mb-10 select-none"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            aria-label="Current word display"
          >
            {/* Fixed Vertical Guides */}
            {/* ORP First-Use Tooltip */}
            {showOrpTooltip && (
              <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs sm:text-sm px-4 py-2.5 rounded-lg shadow-xl max-w-xs">
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-400 dark:text-red-500" />
                  <div>
                    <p className="font-semibold mb-0.5">See the <span className="text-red-400 dark:text-red-500">red letter</span>?</p>
                    <p className="text-[11px] sm:text-xs opacity-90 leading-snug">
                      That's your Optimal Recognition Point — your brain recognizes words fastest here. Keep your eyes fixed on it.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowOrpTooltip(false);
                      localStorage.setItem('flashread_orp_explained', 'true');
                    }}
                    className="ml-1 text-slate-400 dark:text-slate-500 hover:text-white dark:hover:text-slate-900 flex-shrink-0"
                    aria-label="Dismiss ORP explanation"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-slate-900 dark:bg-slate-100 rotate-45"></div>
              </div>
            )}

            <div className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[2px] h-[15%] bg-slate-300 dark:bg-slate-700/50"></div>
            <div className="absolute bottom-[20%] left-1/2 -translate-x-1/2 w-[2px] h-[15%] bg-slate-300 dark:bg-slate-700/50"></div>

            {/* The Word Container */}
            <div className="w-full flex items-baseline justify-center font-mono text-5xl md:text-6xl font-bold tracking-wide">
              {readerDisplayWord ? (
                <>
                  <div className="flex-1 text-right text-slate-900 dark:text-white whitespace-pre">
                    {readerDisplayWord.before}
                  </div>
                  <div className="text-red-500 mx-[1px] relative z-10">
                    {readerDisplayWord.focus}
                  </div>
                  <div className="flex-1 text-left text-slate-900 dark:text-white whitespace-pre">
                    {readerDisplayWord.after}
                  </div>
                </>
              ) : (
                <span className="text-slate-400 dark:text-slate-500 text-3xl">Tap Play</span>
              )}
            </div>

            {effectiveRampConfig.enabled && (
              <div className="absolute top-4 right-4 flex items-center space-x-1 bg-amber-500/20 text-amber-500 px-3 py-1 rounded-full border border-amber-500/30">
                <Zap className="w-3 h-3" />
                <span className="text-xs font-bold uppercase">Auto-Ramp</span>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          <div
            className="w-full h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full mb-10 relative overflow-hidden"
            role="progressbar"
            aria-valuenow={Math.round(readerProgress)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Reading progress"
          >
            <div
              className="h-full bg-primary rounded-full transition-all duration-100 ease-linear"
              style={{ width: `${readerProgress}%` }}
            />
          </div>

          {/* Controls Bar */}
          <div className="flex flex-col md:flex-row items-center justify-center w-full max-w-4xl px-4 gap-6 md:gap-0">
            {/* Simplified Controls for Auth Users: Start, Stop, Restart */}
            <div className="flex items-center justify-center space-x-6">
              {/* Decrease Speed */}
             <button
               type="button"
               onClick={() => readerAdjustWpm(-25)}
               disabled={effectiveRampConfig.enabled}
               className={`flex items-center space-x-2 min-h-[44px] px-3 py-2 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                 effectiveRampConfig.enabled
                   ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                   : 'text-slate-400 hover:text-slate-800 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/50'
               }`}
               title={effectiveRampConfig.enabled ? "Speed adjustment unavailable during auto-ramp" : "Decrease speed (−25 WPM) · or press ←"}
               aria-label="Decrease speed"
             >
                <span className="text-xs font-mono bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 rounded text-slate-600 dark:text-slate-300">←</span>
                <Minus className="w-5 h-5" />
                <span className="text-xs font-semibold uppercase tracking-wider hidden md:inline">Slower</span>
              </button>
  
              {/* Restart */}
              <button
                type="button"
                onClick={readerReset}
                className="flex items-center space-x-2 min-h-[44px] px-3 py-2 text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                title="Restart session"
                aria-label="Restart session"
              >
                <RotateCcw className="w-5 h-5" />
                <span className="text-xs font-semibold uppercase tracking-wider hidden md:inline">Restart</span>
              </button>
  
              {/* Play/Start */}
              <button
                type="button"
                onClick={readerTogglePlay}
                className={`w-16 h-16 flex items-center justify-center rounded-full shadow-xl transition-all transform hover:scale-105 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                  readerStatus === ReaderStatus.PLAYING
                    ? 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white ring-1 ring-slate-200 dark:ring-slate-700'
                    : 'bg-primary text-white shadow-blue-500/30'
                }`}
                aria-label={readerStatus === ReaderStatus.PLAYING ? 'Pause' : 'Play'}
              >
                {readerStatus === ReaderStatus.PLAYING
                  ? <Pause className="w-7 h-7 fill-current" />
                  : <Play className="w-7 h-7 fill-current ml-1" />}
              </button>
  
              {/* Finish/Save */}
              <button
                type="button"
                onClick={() => {
                  readerFinishSession();
                  navigate('/stories');
                }}
                className="flex items-center space-x-2 min-h-[44px] px-3 py-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                title="Finish and save session"
                aria-label="Finish and save session"
              >
                <Square className="w-5 h-5 fill-current" />
                <span className="text-xs font-semibold uppercase tracking-wider hidden md:inline">Finish</span>
              </button>
  
              {/* Increase Speed */}
             <button
               type="button"
               onClick={() => readerAdjustWpm(25)}
               disabled={effectiveRampConfig.enabled}
               className={`flex items-center space-x-2 min-h-[44px] px-3 py-2 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                 effectiveRampConfig.enabled
                   ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                   : 'text-slate-400 hover:text-slate-800 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800/50'
               }`}
               title={effectiveRampConfig.enabled ? "Speed adjustment unavailable during auto-ramp" : "Increase speed (+25 WPM) · or press →"}
               aria-label="Increase speed"
             >
                <Plus className="w-5 h-5" />
                <span className="text-xs font-semibold uppercase tracking-wider hidden md:inline">Faster</span>
                <span className="text-xs font-mono bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 rounded text-slate-600 dark:text-slate-300">→</span>
              </button>
            </div>
          </div>

          {/* Keyboard Shortcuts Hint - always visible */}
          <div className="mt-4 text-center">
            <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center justify-center gap-2">
              <span className="font-semibold">Keyboard shortcuts:</span>
              <span className="font-mono bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 rounded text-slate-600 dark:text-slate-300">←</span>
              <span>slower</span>
              <span className="font-mono bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 rounded text-slate-600 dark:text-slate-300">→</span>
              <span>faster</span>
              <span className="mx-1">·</span>
              <span className="font-mono bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 rounded text-slate-600 dark:text-slate-300">Space</span>
              <span>play/pause</span>
            </p>
          </div>

          {/* Settings Panel - below controls */}
          <div className="w-full max-w-3xl mt-8 mx-auto">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-lg overflow-hidden">
              {/* Settings Header */}
              <button
                type="button"
                onClick={() => setShowSettings(!showSettings)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Settings className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Reading Settings</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showSettings ? 'rotate-180' : ''}`} />
              </button>

              {/* Settings Content */}
              {showSettings && (
                <div className="p-4 border-t border-slate-200 dark:border-slate-700">
                  {/* Speed Mode Selection */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-slate-500 uppercase mb-3">Speed Mode</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setRampEnabled(false)}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${
                          !rampEnabled
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`w-3 h-3 rounded-full border-2 ${!rampEnabled ? 'border-blue-500 bg-blue-500' : 'border-slate-300'}`} />
                          <span className="text-sm font-medium text-slate-800 dark:text-white">Constant</span>
                        </div>
                        <p className="text-xs text-slate-500">Steady pace</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setRampEnabled(true)}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${
                          rampEnabled
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`w-3 h-3 rounded-full border-2 ${rampEnabled ? 'border-blue-500 bg-blue-500' : 'border-slate-300'}`} />
                          <span className="text-sm font-medium text-slate-800 dark:text-white flex items-center">Auto-Ramp <Zap className="w-3 h-3 ml-1 text-amber-500" /></span>
                        </div>
                        <p className="text-xs text-slate-500">Gradual increase</p>
                      </button>
                    </div>
                  </div>

                  {/* Speed Settings */}
                  {rampEnabled ? (
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase mb-1">Start WPM</label>
                        <div className="flex items-center gap-2">
                          <input type="range" min="100" max="1000" step="50" value={rampStart} onChange={(e) => setRampStart(Number(e.target.value))} className="w-full h-1.5 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-600" />
                          <span className="text-xs font-mono w-8 text-right">{rampStart}</span>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase mb-1">End WPM</label>
                        <div className="flex items-center gap-2">
                          <input type="range" min="200" max="2000" step="50" value={rampEnd} onChange={(e) => setRampEnd(Number(e.target.value))} className="w-full h-1.5 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-600" />
                          <span className="text-xs font-mono w-8 text-right">{rampEnd}</span>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase mb-1">Duration</label>
                        <div className="flex items-center gap-2">
                          <input type="range" min="30" max="300" step="30" value={rampDuration} onChange={(e) => setRampDuration(Number(e.target.value))} className="w-full h-1.5 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-600" />
                          <span className="text-xs font-mono w-8 text-right">{rampDuration}s</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Words Per Minute</label>
                      <div className="flex items-center gap-3">
                        <input type="range" min="100" max="2000" step="25" value={constantWpm} onChange={(e) => setConstantWpm(Number(e.target.value))} className="w-48 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-blue-600" />
                        <span className="text-lg font-mono font-bold text-slate-800 dark:text-white">{constantWpm} WPM</span>
                      </div>
                    </div>
                  )}

                  {/* Word Count & Time Preview */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 text-center">
                      <p className="text-xs font-semibold text-slate-500 uppercase">Words</p>
                      <p className="text-lg font-bold text-slate-800 dark:text-white">
                        {(effectiveSections?.reduce((sum, s) => sum + s.wordCount, 0) || (effectiveContent || '').split(/\s+/).filter(w => w.length > 0).length).toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 text-center">
                      <p className="text-xs font-semibold text-slate-500 uppercase">Est. Time</p>
                      <p className="text-lg font-bold text-slate-800 dark:text-white">
                        {(() => {
                          const wordCount = effectiveSections?.reduce((sum, s) => sum + s.wordCount, 0) || (effectiveContent || '').split(/\s+/).filter(w => w.length > 0).length;
                          const avgWpm = rampEnabled ? Math.round((rampStart + rampEnd) / 2) : constantWpm;
                          const totalSeconds = Math.round((wordCount / avgWpm) * 60);
                          const minutes = Math.floor(totalSeconds / 60);
                          const seconds = totalSeconds % 60;
                          if (minutes === 0) return `${seconds}s`;
                          return `${minutes}m ${seconds}s`;
                        })()}
                      </p>
                    </div>
                  </div>

                  {/* Guest Upsell CTA */}
                  {!isAuthenticated && (
                    <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
                      <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                        Sign up to unlock speed customization, save progress, and access 500+ stories.
                      </p>
                      <button
                        onClick={() => { readerFinishSession(); navigate('/signup'); }}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        Sign Up to Unlock All Features
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
    </div>
  );
};

export default RSVPReader;
